import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useUser } from '@r/api/auth.tsx';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { type FC, useContext, useMemo } from 'react';
import { isTypologyConversion } from '@r/utils/workspace-utils.ts';
import {
  useApiCountriesGetCollection,
  useApiAdmintypologiesGetCollection,
  useApiAdminusersGetCollection,
  apiAdminusersGetCollection,
  useApiCompaniesIdPatch,
  useApiAdmincompaniesCompanyIdresponsiblePost,
  useApiAdmincompaniesCompanyIdresponsibleResponsibleIdDelete,
  getApiAdmincompaniesIdGetQueryKey,
  getApiAdmincompaniesGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type {
  TypologyTypologyReadModel,
  UserUserCollectionReadModel,
  CompanyCompanyReadModel
} from '@r/api/generated/client.schemas.ts';
import type { ApiCountry } from '@ui/country-dropdown.tsx';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import { CalendarDays, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import type { Option } from '@r/types/index.ts';
import { CountryDropdown } from '@ui/country-dropdown.tsx';
import { TypologyDropdown } from '@ui/typology-dropdown.tsx';
import { MultiSelect, type MultiSelectOption } from '@ui/multi-select.tsx';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { roleToI18nKey, type Role } from '@r/utils/roles.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

const COMMENT_MAX_LENGTH = 255;
const COMPANY_RESPONSIBLES_LIMIT = 10;
const USER_WORKSPACE_IRI_PREFIX = '/user_workspaces';

type UpdateCompanyFormProps = CompanyCompanyReadModel & {
  formMode?: 'full' | 'responsible-only';
  showResponsibleField?: boolean;
  onSuccess?: () => void;
};

export const UpdateCompanyForm: FC<UpdateCompanyFormProps> = ({
  formMode = 'full',
  showResponsibleField: _showResponsibleField = true,
  onSuccess,
  ...companyData
}) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const queryClient = useQueryClient();

  const [selectedCountryOption, setSelectedCountryOption] = useState<Option | null>(null);

  const userWorkspaces = user.data?.visibleWorkspaces ?? [];
  const activeWorkspace = getActiveWorkspaceUniversal(userWorkspaces);
  const isUserSuperAdmin = activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN') || false;
  const isUserAdmin = activeWorkspace?.roles?.includes('ROLE_ADMIN') || isUserSuperAdmin || false;
  const canEditResponsible = isUserSuperAdmin || isUserAdmin;
  const canAccessTypologies = isUserAdmin;

  const {
    data: countriesResponse,
    isLoading: isCountriesLoading,
    isError: isCountriesError
  } = useApiCountriesGetCollection();

  const {
    data: typologiesResponse,
    isLoading: isTypologiesLoading,
    isError: isTypologiesError = false
  } = useApiAdmintypologiesGetCollection(undefined, {
    query: { enabled: canAccessTypologies && isTypologyConversion(workspaceConfigContext) }
  });

  const countries: ApiCountry[] = (countriesResponse?.data || []) as unknown as ApiCountry[];
  const currentCountryAlpha3 = companyData.country?.alpha3 || '';

  const typologies: TypologyTypologyReadModel[] = typologiesResponse?.data || [];
  const currentTypologyName = companyData.typology?.name || null;
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);
  const initialResponsibleUniqueCodes = useMemo(
    () =>
      (companyData.responsibles ?? [])
        .map((responsible) => responsible.uniqueCode)
        .filter((uniqueCode): uniqueCode is string => Boolean(uniqueCode)),
    [companyData.responsibles]
  );
  const [selectedResponsibleUniqueCodes, setSelectedResponsibleUniqueCodes] = useState<string[]>(
    initialResponsibleUniqueCodes
  );

  const responsibleQuery = useApiAdminusersGetCollection(
    {
      search: debouncedQuery,
      perPage: 100,
      'maxRole[]': ['ROLE_SALES_ADMIN']
    },
    {
      query: {
        enabled:
          canEditResponsible && (formMode === 'responsible-only' || debouncedQuery.length > 0)
      }
    }
  );

  const FormSchema = z.object({
    name: z.string().min(1, t('form.fields.required')).max(100),
    uniqueCode: z
      .string()
      .min(1, t('form.fields.required'))
      .max(50)
      .regex(/^([a-zA-Z0-9]+)$/)
      .describe(t('form.fields.schema.describe.unique.code')),
    siren: z
      .union([z.string(), z.null()])
      .describe(t('form.fields.schema.describe.siren'))
      .optional(),
    tva: z.union([z.string(), z.null()]).describe(t('form.fields.schema.describe.tva')).optional(),
    addressLine1: z
      .string()
      .min(1, t('form.fields.required'))
      .max(255)
      .describe(t('form.fields.schema.describe.address.line')),
    addressLine2: z
      .union([z.string(), z.null()])
      .optional()
      .describe(t('form.fields.schema.describe.address.line')),
    addressLine3: z
      .union([z.string(), z.null()])
      .optional()
      .describe(t('form.fields.schema.describe.address.line')),
    zipcode: z
      .string()
      .min(1, t('form.fields.required'))
      .max(20)
      .describe(t('form.fields.schema.describe.zipcode')),
    city: z
      .string()
      .min(1, t('form.fields.required'))
      .max(100)
      .describe(t('form.fields.schema.describe.city')),
    countryAlpha3: z.string().min(1, t('form.fields.required')),
    typologyName: z.union([z.string(), z.null()]).optional(),
    pointCoeff: z
      .union([
        z.coerce.number({
          message: t('company.create.point.coeff.invalid'),
          invalid_type_error: t('company.create.point.coeff.invalid')
        }),
        z.undefined()
      ])
      .describe(t('form.fields.schema.describe.point.coeff'))
      .refine((val) => {
        if (isTypologyConversion(workspaceConfigContext)) {
          return true;
        }
        return val !== undefined && val > 0;
      }, t('company.create.point.coeff.required'))
      .refine((val) => {
        if (val === undefined) return true;
        const decimalPlaces = val.toString().split('.')[1]?.length || 0;
        return decimalPlaces <= 3;
      }, t('company.create.point.coeff.decimal.max')),
    comment: z
      .string()
      .max(COMMENT_MAX_LENGTH, t('company.comment.max.length', { max: COMMENT_MAX_LENGTH }))
      .optional()
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: companyData.name,
      uniqueCode: companyData.uniqueCode,
      siren: companyData.siren,
      tva: companyData.tva,
      addressLine1: companyData.addressLine1,
      addressLine2: companyData.addressLine2,
      addressLine3: companyData.addressLine3,
      zipcode: companyData.zipcode,
      city: companyData.city,
      countryAlpha3: currentCountryAlpha3,
      typologyName: currentTypologyName,
      pointCoeff: companyData.pointCoeff ?? undefined,
      comment: companyData.comment ?? ''
    }
  });

  useEffect(() => {
    if (countries.length > 0 && companyData.country) {
      const countryMatch = countries.find(
        (country) => country.alpha3 === companyData.country?.alpha3
      );

      if (countryMatch) {
        const initialOption = {
          label: countryMatch.name,
          value: countryMatch.alpha3
        };

        setSelectedCountryOption(initialOption);
        form.setValue('countryAlpha3', initialOption.value);
      }
    }
  }, [countries, companyData.country, form]);

  useEffect(() => {
    setSelectedResponsibleUniqueCodes(initialResponsibleUniqueCodes);
  }, [initialResponsibleUniqueCodes]);

  const companyMutation = useApiCompaniesIdPatch({
    mutation: {
      onSuccess: (updatedCompany) => {
        const companyId = companyData.id?.toString();
        if (companyId) {
          queryClient.setQueryData(getApiAdmincompaniesIdGetQueryKey(companyId), updatedCompany);
        }
        // Invalide la liste pour forcer un refetch cohérent
        queryClient.invalidateQueries({ queryKey: getApiAdmincompaniesGetCollectionQueryKey() });

        toast.success(t('form.company.informations.success'));
        onSuccess?.();
      },
      onError: (error) => {
        showOrvalErrorToast(
          error,
          t('form.error.message', {
            errorMessage: t('form.company.informations.error')
          })
        );
      }
    }
  });

  const addResponsibleMutation = useApiAdmincompaniesCompanyIdresponsiblePost();
  const removeResponsibleMutation = useApiAdmincompaniesCompanyIdresponsibleResponsibleIdDelete();

  const responsibleWorkspaceIdsByUniqueCode = new Map<string, number>();

  for (const responsible of companyData.responsibles ?? []) {
    const responsibleId = (responsible as typeof responsible & { id?: number }).id;

    if (responsible.uniqueCode && responsibleId) {
      responsibleWorkspaceIdsByUniqueCode.set(responsible.uniqueCode, responsibleId);
    }
  }

  for (const responsible of responsibleQuery.data?.data ?? []) {
    const uw = getActiveWorkspaceUniversal(responsible.userWorkspaces ?? []);

    if (uw?.uniqueCode && uw.id) {
      responsibleWorkspaceIdsByUniqueCode.set(uw.uniqueCode, uw.id);
    }
  }

  const responsibleOptions = [
    ...(companyData.responsibles ?? []).map((responsible) => ({
      label: responsible.user.fullname ?? '',
      value: responsible.uniqueCode,
      description: responsible.uniqueCode ?? undefined
    })),
    ...(responsibleQuery.data?.data ?? []).map((responsible: UserUserCollectionReadModel) => {
      const uw = getActiveWorkspaceUniversal(responsible.userWorkspaces ?? []);
      return {
        label: responsible.fullname || `${responsible.firstname} ${responsible.lastname}`,
        value: uw?.uniqueCode ?? '',
        description: uw?.maxRole ? t(roleToI18nKey(uw.maxRole as Role)) : undefined,
        disabled: !uw?.id
      };
    })
  ].reduce<MultiSelectOption[]>((options, option) => {
    if (!option.value || options.some((existingOption) => existingOption.value === option.value)) {
      return options;
    }

    options.push(option);
    return options;
  }, []);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const companyId = companyData.id as number;
    companyMutation.mutate({ id: companyId.toString(), data });
  }

  const getResponsibleWorkspaceId = async (uniqueCode: string): Promise<number | undefined> => {
    const knownResponsibleId = responsibleWorkspaceIdsByUniqueCode.get(uniqueCode);
    if (knownResponsibleId) return knownResponsibleId;

    const response = await apiAdminusersGetCollection({
      'userWorkspaces.uniqueCode': uniqueCode,
      'maxRole[]': ['ROLE_SALES_ADMIN'],
      perPage: 1
    });
    const users = response.data as UserUserCollectionReadModel[];
    const matchingWorkspace = users
      ?.flatMap((responsible) => responsible.userWorkspaces ?? [])
      .find((workspace) => workspace.uniqueCode === uniqueCode);

    return matchingWorkspace?.id;
  };

  const handleSubmitResponsibles = async () => {
    if (!companyData.id) return;

    const companyId = companyData.id;
    const nextResponsibleUniqueCodes = selectedResponsibleUniqueCodes.slice(
      0,
      COMPANY_RESPONSIBLES_LIMIT
    );
    const responsibleUniqueCodesToAdd = nextResponsibleUniqueCodes.filter(
      (uniqueCode) => !initialResponsibleUniqueCodes.includes(uniqueCode)
    );
    const responsibleUniqueCodesToRemove = initialResponsibleUniqueCodes.filter(
      (uniqueCode) => !nextResponsibleUniqueCodes.includes(uniqueCode)
    );

    try {
      await Promise.all(
        responsibleUniqueCodesToRemove.map(async (uniqueCode) => {
          const responsibleId = await getResponsibleWorkspaceId(uniqueCode);
          if (!responsibleId) throw new Error(`Missing user workspace id for ${uniqueCode}`);

          return removeResponsibleMutation.mutateAsync({
            companyId,
            responsibleId
          });
        })
      );
      await Promise.all(
        responsibleUniqueCodesToAdd.map(async (uniqueCode) => {
          const responsibleId = await getResponsibleWorkspaceId(uniqueCode);
          if (!responsibleId) throw new Error(`Missing user workspace id for ${uniqueCode}`);

          return addResponsibleMutation.mutateAsync({
            companyId,
            data: { responsible: `${USER_WORKSPACE_IRI_PREFIX}/${responsibleId}` }
          });
        })
      );

      queryClient.invalidateQueries({
        queryKey: getApiAdmincompaniesIdGetQueryKey(companyId.toString())
      });
      queryClient.invalidateQueries({ queryKey: getApiAdmincompaniesGetCollectionQueryKey() });
      toast.success(t('form.company.informations.success'));
      onSuccess?.();
    } catch (error) {
      showOrvalErrorToast(
        error,
        t('form.error.message', {
          errorMessage: t('form.company.informations.error')
        })
      );
    }
  };

  const responsiblesLabel = t('label.company.responsible');

  if (formMode === 'responsible-only') {
    return (
      <Form {...form} schema={FormSchema}>
        <form
          onSubmit={form.handleSubmit(handleSubmitResponsibles)}
          className="mx-auto space-y-6 w-full"
        >
          <FormItem className="flex flex-col">
            <FormLabel>{responsiblesLabel}</FormLabel>
            {!canEditResponsible ? (
              <Input
                placeholder={responsiblesLabel}
                value={
                  (companyData.responsibles ?? [])
                    .map((responsible) => responsible.user.fullname)
                    .join(', ') || '-'
                }
                disabled={true}
                className={'cursor-not-allowed'}
              />
            ) : (
              <MultiSelect
                placeholder={t('common.label.search.by', { field: responsiblesLabel })}
                searchPlaceholder={t('common.label.search.by', { field: responsiblesLabel })}
                options={responsibleOptions}
                value={selectedResponsibleUniqueCodes}
                onValueChange={(responsibleUniqueCodes) =>
                  setSelectedResponsibleUniqueCodes(
                    responsibleUniqueCodes.slice(0, COMPANY_RESPONSIBLES_LIMIT)
                  )
                }
                searchValue={query}
                onSearchChange={setQuery}
                loading={responsibleQuery.isLoading}
                maxSelected={COMPANY_RESPONSIBLES_LIMIT}
                maxSelectedMessage={`Limite de ${COMPANY_RESPONSIBLES_LIMIT} responsables atteinte.`}
              />
            )}
            <FormMessage className="mt-1 text-xs" />
          </FormItem>
          <Button
            rounded="full"
            loading={addResponsibleMutation.isPending || removeResponsibleMutation.isPending}
            className="w-full"
            type="submit"
          >
            <Save className="mr-2 w-4 h-4" />
            {t('form.save.modifications')}
          </Button>
        </form>
      </Form>
    );
  }

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mx-auto space-y-10 w-full 2xl:w-8/12">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem className="flex flex-col">
              <FormControl>
                <Input
                  placeholder={t('label.company.name')}
                  {...field}
                  error={!!fieldState.error}
                  className="text-3xl font-bold"
                />
              </FormControl>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>
          )}
        />

        <div className="space-y-5">
          <div className="border-b-2 border-muted-foreground/40 pb-1.5 text-muted-foreground/80 font-medium">
            {t('admin.company.profile.section.legal.info')}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="siren"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.siren')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.siren')}
                      {...field}
                      value={field.value ?? ''}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tva"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.tva')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.tva')}
                      {...field}
                      value={field.value ?? ''}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />
          </div>

          <div className="border-b-2 border-muted-foreground/40 pb-1.5 text-muted-foreground/80 font-medium">
            {t('admin.company.profile.section.program.info')}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="uniqueCode"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.unique.code')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.unique.code')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />

            {isTypologyConversion(workspaceConfigContext) ? (
              <FormField
                control={form.control}
                name="typologyName"
                render={({ field, fieldState }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('label.company.typology')}</FormLabel>
                    <FormControl>
                      {canAccessTypologies && !isTypologiesError ? (
                        <TypologyDropdown
                          placeholder={t('label.company.typology')}
                          defaultValue={field.value ?? undefined}
                          loading={isTypologiesLoading}
                          options={typologies}
                          onChange={(typology) => {
                            field.onChange(typology?.name ?? null);
                          }}
                        />
                      ) : (
                        <Input
                          placeholder={t('label.company.typology')}
                          {...field}
                          error={!!fieldState.error}
                          value={field.value ?? ''}
                          disabled={true}
                          className={'cursor-not-allowed'}
                        />
                      )}
                    </FormControl>
                    <FormMessage className="mt-1 text-xs" />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="pointCoeff"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t('company.profile.point.coefficient')}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={t('company.profile.point.coefficient')}
                        {...field}
                        error={!!fieldState.error}
                      />
                    </FormControl>
                    <FormMessage className="mt-1 text-xs" />
                  </FormItem>
                )}
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="justify-center text-sm">
              <h3 className="mb-2 font-medium">{t('label.company.created.at')}</h3>
              <div className="flex items-center py-4">
                <CalendarDays className="mx-2 w-5 h-5" />
                <span>
                  {t('company.created.at', {
                    createdAt: format(new Date(companyData.createdAt), 'dd/MM/yyyy')
                  })}
                </span>
              </div>
            </div>
          </div>

          <FormField
            control={form.control}
            name="comment"
            render={({ field, fieldState }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('label.company.comment')}</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder={t('company.comment.placeholder')}
                    {...field}
                    value={field.value ?? ''}
                    maxLength={COMMENT_MAX_LENGTH}
                    rows={4}
                    className="resize-none"
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage className="mt-1 text-xs" />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-5">
          <div className="border-b-2 border-muted-foreground/40 pb-1.5 text-muted-foreground/80 font-medium">
            {t('admin.company.profile.section.postal.address')}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="addressLine1"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.address.line.1')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.address.line.1')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="zipcode"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.zipcode')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.zipcode')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="addressLine2"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.address.line.2')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.address.line.2')}
                      {...field}
                      value={field.value ?? ''}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.city')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.city')}
                      {...field}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="addressLine3"
              render={({ field, fieldState }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t('label.company.address.line.3')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('label.company.address.line.3')}
                      {...field}
                      value={field.value ?? ''}
                      error={!!fieldState.error}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />

            {!isCountriesError && (
              <FormField
                control={form.control}
                name="countryAlpha3"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t('label.company.country')}</FormLabel>
                    <CountryDropdown
                      placeholder={t('label.company.country')}
                      defaultValue={selectedCountryOption?.value}
                      loading={isCountriesLoading}
                      options={countries}
                      onChange={(country) => {
                        field.onChange(country.alpha3);
                      }}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>

        <Button rounded="full" loading={companyMutation.isPending} className="w-full" type="submit">
          <Save className="mr-2 w-4 h-4" />
          {t('form.save.modifications')}
        </Button>
      </form>
    </Form>
  );
};
